import React, { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { USER_ROLES } from "../config/authRoles";
import {
  LayoutDashboard,
  Building2,
  Users,
  DollarSign,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  User,
  Wallet,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, roles, hasRole, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Centralized menu configuration - easily modifiable for different roles
  const menuConfig = [
    {
      path: "/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
      allowedRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TA_ADMIN,
        USER_ROLES.TA_USER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.EMPLOYEE,
      ],
    },
    {
      path: "/companies",
      icon: Building2,
      label: "Companies",
      allowedRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TA_ADMIN,
        USER_ROLES.TA_USER,
      ],
    },
    // {
    //   path: "/company",
    //   icon: Building2,
    //   label: "Company",
    //   allowedRoles: [
    //     USER_ROLES.SUPER_ADMIN,
    //     USER_ROLES.TA_ADMIN,
    //     USER_ROLES.TA_USER,
    //   ],
    // },
    {
      path: "/employees",
      icon: Users,
      label: "Employees",
      allowedRoles: Object.values(USER_ROLES),
    },
    {
      path: "/payroll",
      icon: DollarSign,
      label: "Payroll",
      allowedRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TA_ADMIN,
        USER_ROLES.TA_USER,
        USER_ROLES.ORG_ADMIN,
      ],
    },
    {
      path: "/finance",
      icon: Wallet,
      label: "Finance",
      allowedRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TA_ADMIN,
        USER_ROLES.TA_USER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.EMPLOYEE,
      ],
    },
    {
      path: "/leaves",
      icon: Calendar,
      label: "Leaves",
      allowedRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TA_ADMIN,
        USER_ROLES.TA_USER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.EMPLOYEE,
      ],
    },
    // {
    //   path: "/reimbursements",
    //   icon: Receipt,
    //   label: "Reimbursements",
    //   allowedRoles: [
    //     USER_ROLES.SUPER_ADMIN,
    //     USER_ROLES.TA_ADMIN,
    //     USER_ROLES.TA_USER,
    //     USER_ROLES.ORG_ADMIN,
    //     USER_ROLES.EMPLOYEE,
    //   ],
    // },
    {
      path: "/reports",
      icon: FileText,
      label: "Reports",
      allowedRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TA_ADMIN,
        USER_ROLES.TA_USER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.EMPLOYEE,
      ],
    },
    {
      path: "/settings",
      icon: Settings,
      label: "Company Profile",
      allowedRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TA_ADMIN,
        USER_ROLES.TA_USER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.EMPLOYEE,
      ],
    },
  ];

  const visibleMenuItems = menuConfig.filter((item) =>
    hasRole(item.allowedRoles),
  );

  const roleLabel =
    roles && roles.length > 0
      ? roles
          .map((r) =>
            r
              .split("_")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" "),
          )
          .join(", ")
      : "User";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="px-4 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>

            <Link to="/dashboard" className="flex items-center space-x-2">
              <svg
                className="w-8 h-8 text-blue-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
              </svg>
              <div className="flex items-center">
                <span className="text-xl font-bold text-gray-900">ApurvaX</span>
                <span className="text-xl text-gray-500 ml-1">Payroll</span>
              </div>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                    {user?.name?.charAt(0) || "A"}
                  </div>
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium">
                      {user?.name || "User"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {roleLabel} {user?.company ? `. ${user.company}` : ""}
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-gray-200 transition-transform duration-300 z-40 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <nav className="p-4 space-y-1">
          {visibleMenuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(`${item.path}/`);
            return (
              <Link
                key={`${item.path}-${index}`}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 mt-16 p-6">
        <Outlet />
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout;
